import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, Lightbulb, Code, Clock } from 'lucide-react';

const issues = [
  {
    category: 'Workflow Execution',
    items: [
      {
        issue: 'Workflow won\'t run',
        solutions: [
          'Check the Validation panel for errors',
          'Ensure at least one trigger node is present',
          'Verify all required fields are configured',
          'Check that nodes are properly connected',
        ],
      },
      {
        issue: 'No output is visible',
        solutions: [
          'Ensure action nodes are connected to other nodes',
          'Check the Logs tab for error messages',
          'Verify that nodes have been configured',
          'Click Run again and check the Results panel',
        ],
      },
      {
        issue: 'Workflow throws an error',
        solutions: [
          'Review the error message in the Logs tab',
          'Check node configuration for invalid inputs',
          'Verify HTTP URLs are correct and accessible',
          'Test with simpler workflows first',
        ],
      },
    ],
  },
  {
    category: 'Data & Transformation',
    items: [
      {
        issue: 'Data not transforming correctly',
        solutions: [
          'Check the transformation JavaScript code for syntax errors',
          'Verify input data format matches expectations',
          'Use console.log() to debug transformations',
          'Test with simpler transformations first',
        ],
      },
      {
        issue: 'HTTP request fails',
        solutions: [
          'Verify the URL is correct and accessible',
          'Check headers are properly formatted JSON',
          'Test the endpoint in a REST client first',
          'Ensure API authentication is configured',
        ],
      },
      {
        issue: 'JavaScript code error in transform',
        solutions: [
          'Check for syntax errors in JavaScript',
          'Use proper variable names and references',
          'Test code in browser console first',
          'Ensure the function returns a valid value',
        ],
      },
    ],
  },
  {
    category: 'Connections & Routing',
    items: [
      {
        issue: 'Nodes won\'t connect',
        solutions: [
          'Try using the Connect Node button on the node panel',
          'Ensure both nodes are on the canvas',
          'Verify connection order is correct (left to right)',
          'Check that nodes are not trying to connect to themselves',
        ],
      },
      {
        issue: 'Data not flowing through workflow',
        solutions: [
          'Check edge connections in the Workflows tab',
          'Verify nodes are properly connected',
          'Look for circular dependencies in Validation panel',
          'Test with a simple linear workflow',
        ],
      },
    ],
  },
];

export function TroubleshootingGuide() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Troubleshooting Guide</h2>
        <p className="text-muted-foreground">
          Find solutions to common workflow automation issues
        </p>
      </div>

      <Tabs defaultValue={issues[0].category} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          {issues.map((category) => (
            <TabsTrigger key={category.category} value={category.category}>
              {category.category}
            </TabsTrigger>
          ))}
        </TabsList>

        {issues.map((category) => (
          <TabsContent key={category.category} value={category.category} className="space-y-4">
            {category.items.map((item, idx) => (
              <Card key={idx} className="p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground">{item.issue}</h4>
                  </div>
                </div>

                <div className="space-y-2 pl-8">
                  <p className="text-sm font-medium text-muted-foreground">Solutions:</p>
                  <ul className="space-y-2">
                    {item.solutions.map((solution, sIdx) => (
                      <li key={sIdx} className="flex items-start gap-2 text-sm">
                        <span className="text-green-600 font-bold">✓</span>
                        <span className="text-muted-foreground">{solution}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            ))}
          </TabsContent>
        ))}
      </Tabs>

      <Card className="p-4 bg-green-500/5 border-green-500/20 space-y-3">
        <div className="flex items-start gap-2">
          <Lightbulb className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-foreground">Best Practices</h4>
            <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
              <li>• Start with simple workflows before building complex ones</li>
              <li>• Use the Validation panel regularly to catch issues early</li>
              <li>• Test workflows frequently with the Run button</li>
              <li>• Check logs after each run to track data flow</li>
              <li>• Use meaningful node names for better organization</li>
              <li>• Break complex workflows into smaller, testable parts</li>
            </ul>
          </div>
        </div>
      </Card>

      <Card className="p-4 bg-blue-500/5 border-blue-500/20 space-y-3">
        <div className="flex items-start gap-2">
          <Code className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-foreground">Debugging Tips</h4>
            <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
              <li>• Add Log nodes at key points to track data</li>
              <li>• Use simple test data first before complex scenarios</li>
              <li>• Test HTTP endpoints separately before adding to workflow</li>
              <li>• Use browser DevTools console to test JavaScript code</li>
              <li>• Check the Logs tab for detailed error messages</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}
